import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, PasswordField, TextField, Typography } from 'seagull';
import { requiredValidation, emailValidation } from 'validators';
import { routes } from "../../../../constants";
import { useStoreActions } from "../../../../contexts";
import { useEventReporter } from "../../../../event-reporter";
import { useIsMounted } from "../../../../hooks/useIsMounted";
import { handlePromise } from "../../../../infra/utils";
import { FormSeparatorHorizontal } from "../../components/FormSeparator";
import { ErrorMessage, FooterLink, FormContainer, FormFields, VerticalFormFields, FormSubTitle, SubmitButtonField, LinkButton } from "../../components/FormComponents";
import styles from "./Password.module.scss";
import sharedStyles from "../LoginScreen.module.scss";
import { useUtmsParams } from "../../../../hooks/useUtmsParams";
import { useLocationCommonActions } from "../../../../providers/LocationProvider";
const genericErrorMessage = 'Failed to login, please try again';
export default function PasswordStep(_ref) {
  let {
    email,
    onClickRequestOTP,
    titleMP,
    subtitleMP
  } = _ref;
  const [errorMessage, setErrorMessage] = useState();
  const {
    handleSubmit,
    control,
    formState,
    setFocus
  } = useForm({
    mode: 'onChange'
  });
  const {
    api,
    logBI,
    setAuthToken
  } = useStoreActions();
  const [isLoading, setIsLoading] = useState(false);
  const {
    reportFormSubmission
  } = useEventReporter();
  const isMounted = useIsMounted();
  const getUtms = useUtmsParams();
  const {
    getPageUrl
  } = useLocationCommonActions();

  async function onPasswordFormSubmit(_ref2) {
    let {
      email,
      password
    } = _ref2;
    logBI(10254, {});
    setErrorMessage(undefined);
    setIsLoading(true);
    const [response, loginErr] = await handlePromise(api.auth.loginWithPassword({
      email,
      password,
      utms: getUtms(),
      anchorReferrer: getPageUrl()
    }));

    if (loginErr || !response || !response.authToken) {
      var _loginErr$response;

      if ((loginErr === null || loginErr === void 0 ? void 0 : (_loginErr$response = loginErr.response) === null || _loginErr$response === void 0 ? void 0 : _loginErr$response.data) === 'failed to login auth service') {
        setErrorMessage('Failed to login, please try again or reset your password');
      } else {
        setErrorMessage(genericErrorMessage);
      }
    } else {
      reportFormSubmission('general-logged-in-successfully', null);
      const [, authTokenError] = await handlePromise(setAuthToken(response.authToken));

      if (authTokenError) {
        setErrorMessage(genericErrorMessage);
      }
    }

    if (isMounted()) {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setFocus('password');
  }, [setFocus]);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormContainer, {
    onSubmit: handleSubmit(onPasswordFormSubmit),
    "data-testid": "login-form"
  }, titleMP, subtitleMP, /*#__PURE__*/React.createElement(FormFields, null, /*#__PURE__*/React.createElement(VerticalFormFields, null, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "email",
    defaultValue: email,
    rules: emailValidation(undefined, {
      required: true
    }),
    render: _ref3 => {
      var _fieldState$error;

      let {
        field: {
          onChange,
          onBlur,
          ref,
          value
        },
        fieldState
      } = _ref3;
      return /*#__PURE__*/React.createElement(TextField, {
        type: "email",
        label: "Work email",
        inputRef: ref,
        error: fieldState.isTouched && !!fieldState.error,
        helperText: fieldState.isTouched && ((_fieldState$error = fieldState.error) === null || _fieldState$error === void 0 ? void 0 : _fieldState$error.message),
        "data-testid": "email-input",
        value: value,
        onBlur: onBlur,
        onChange: onChange,
        placeholder: "Work email",
        disabled: isLoading,
        autoComplete: "username"
      });
    }
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "password",
    defaultValue: "",
    rules: requiredValidation(true),
    render: _ref4 => {
      var _fieldState$error2;

      let {
        field: {
          onChange,
          onBlur,
          ref,
          value
        },
        fieldState
      } = _ref4;
      return /*#__PURE__*/React.createElement(PasswordField, {
        label: "Password",
        inputRef: ref,
        value: value,
        "data-testid": "password-input",
        error: !!fieldState.error,
        helperText: (_fieldState$error2 = fieldState.error) === null || _fieldState$error2 === void 0 ? void 0 : _fieldState$error2.message,
        onBlur: onBlur,
        onChange: onChange,
        placeholder: "Password",
        disabled: isLoading,
        autoComplete: "current-password"
      });
    }
  }), /*#__PURE__*/React.createElement(Typography, {
    className: styles.forgotYourPassword,
    as: "p",
    variant: "paragraph2",
    skin: "secondary"
  }, "Forgot your password?", ' ', /*#__PURE__*/React.createElement(FooterLink, {
    toInternalLink: routes.resetPassword,
    "data-testid": "reset-password-link"
  }, "Reset it here")), errorMessage && /*#__PURE__*/React.createElement(ErrorMessage, {
    "data-testid": "err-message"
  }, errorMessage)), /*#__PURE__*/React.createElement(SubmitButtonField, {
    disabled: !formState.isValid,
    isLoading: isLoading,
    "data-testid": "login-button"
  }, "Sign in")))), /*#__PURE__*/React.createElement(FormSeparatorHorizontal, null, "Or"), /*#__PURE__*/React.createElement("div", {
    className: styles.otpContainer
  }, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "request-otp-button",
    skin: "primaryOutline",
    onClick: () => onClickRequestOTP()
  }, "Get a one-time code"), /*#__PURE__*/React.createElement(FormSubTitle, null, "We'll email you a six digit code to sign in without a password.")), /*#__PURE__*/React.createElement("div", {
    className: sharedStyles.footer
  }, /*#__PURE__*/React.createElement(Typography, {
    as: "p",
    variant: "paragraph2",
    skin: "secondary"
  }, "For a one-time access link,", ' ', /*#__PURE__*/React.createElement(FooterLink, {
    toInternalLink: routes.requestALink,
    "data-testid": "request-a-link-link"
  }, "click here")), /*#__PURE__*/React.createElement(LinkButton, {
    toInternalLink: routes.login,
    "data-testid": "back-link",
    className: sharedStyles.backButton
  }, "Back")));
}